import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AgentPanel, conversationKeys } from "../../../agent";
import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { SmeCardView } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { useLock } from "../../../live/store";
import { Banner } from "../../../ui/Banner";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { Loading } from "../../../ui/EmptyState";
import { cardPath } from "../inbox/InboxEntryRow";
import { formatWhen } from "../shared/format";
import { AnswerDialog } from "./AnswerDialog";
import { BeforeAfter } from "./BeforeAfter";
import { CardHead } from "./CardHead";
import { ChecksBlock, SymbolsBlock, UsedIn } from "./ChecksBlock";
import { DecisionBar } from "./DecisionBar";
import { ExplainChips } from "./ExplainChips";
import { HistoryBlock } from "./HistoryBlock";
import { LineList } from "./LineList";
import { SourceBlock, WhyBlock } from "./SourceBlock";
import { Stepper } from "./Stepper";
import { useWalk } from "./useWalk";
import { useCardAnswers } from "./useCardAnswers";
import { useCardDisplay } from "./useCardDisplay";

function LockBanner({ elementId }: { elementId: string }) {
  const me = useCurrentMe();
  const lock = useLock(elementId);
  if (!lock || lock.holder.membership_id === me.active?.membership_id) return null;
  return (
    <Banner>
      Being edited by {lock.holder.display}
      {lock.expires_at ? ` (until ${formatWhen(lock.expires_at)} at the latest)` : ""}. Validate, Send back and Reject are refused while someone edits it; a note is always allowed. You can try again later.
    </Banner>
  );
}

/** The card, once it has been read. Keyed by element, so a step to the next card starts fresh. */
function CardBody({ card }: { card: SmeCardView }) {
  const navigate = useNavigate();
  const me = useCurrentMe();
  const display = useCardDisplay(card);
  const walk = useWalk(card);
  // A card the SME had looked at, and that changed under them, must be shown again before Validate opens.
  const [wasShown, setWasShown] = useState(card.displayed);
  // The helper's panel opens when it is wanted (a question, or "write your own"), so the page does not jump to it.
  const [helperOpen, setHelperOpen] = useState(false);
  if (card.displayed && !wasShown) setWasShown(true);

  const answers = useCardAnswers(card, {
    // The decision takes the card out of the inbox: go on to the next card, or back to the inbox after the last one.
    onSettled: () => {
      const next = walk.nextCardId;
      void navigate(next ? cardPath(next) : "/inbox", next ? { state: { shown: true } } : undefined);
    },
  });
  const { element } = card;
  const membershipId = me.active?.membership_id;

  return (
    <div className="ra-card-page">
      <div className="ra-card-main stack">
        <Stepper card={card} />
        <LockBanner elementId={element.id} />
        {wasShown && !card.displayed && (
          <Banner>This card changed since you looked at it. Show it again before you validate.</Banner>
        )}
        <CardHead card={card} />
        <BeforeAfter before={card.before} after={card.after} title={element.pending ? "Before and after" : "What is proposed"} />
        <SourceBlock card={card} />
        <WhyBlock card={card} />
        <LineList
          title="Library term"
          titleId="ra-library-title"
          lines={card.library_lines}
          card={card}
          busy={answers.busy}
          empty="No library line is on this card."
          onAnswer={answers.openLine}
        />
        <LineList title="Lines and values inside this card" titleId="ra-lines-title" lines={card.lines} card={card} busy={answers.busy} onAnswer={answers.openLine} />
        <SymbolsBlock card={card} />
        <ChecksBlock card={card} />
        <UsedIn count={card.used_in} />
        <HistoryBlock history={card.history} notes={card.notes} />
        <DecisionBar
          card={card}
          busy={answers.busy}
          showing={display.busy}
          showError={display.error}
          onShow={() => void display.show()}
          onValidate={() => void answers.validateElement()}
          onAnswer={answers.openElement}
        />
        {!answers.target && answers.problem && (
          <p role="alert" className="form-error">
            {answers.problem}
          </p>
        )}
      </div>

      <aside className="ra-card-side stack" aria-label="Review helper">
        <ExplainChips elementId={element.id} onAsked={() => setHelperOpen(true)} />
        {membershipId &&
          (helperOpen ? (
            <>
              <AgentPanel
                conversationKey={conversationKeys.sme(membershipId)}
                mode="sme_review"
                context={{ screen: "sme_inbox", element_id: element.id }}
                title="Review helper"
                emptyHint="Use one of the questions above, or write your own."
              />
              <Button variant="quiet" onClick={() => setHelperOpen(false)}>
                Hide the review helper
              </Button>
            </>
          ) : (
            <Button variant="quiet" onClick={() => setHelperOpen(true)}>
              Write your own question
            </Button>
          ))}
      </aside>

      <AnswerDialog
        card={card}
        target={answers.target}
        busy={answers.busy}
        error={answers.problem}
        onConfirm={(text, lineId) => void answers.confirm(text, lineId)}
        onCancel={answers.close}
      />
    </div>
  );
}

/** One element to validate, send back or reject (ui-direction screen 4, architecture 12.5). */
export function CardPage() {
  const { elementId = "" } = useParams();
  const query = useApiQuery("sme_card", { element_id: elementId }, { enabled: elementId !== "" });

  if (query.isPending) return <Loading>Reading the card</Loading>;
  if (query.isError || !query.data) {
    return (
      <Card className="stack" aria-labelledby="ra-card-error-title">
        <h1 id="ra-card-error-title">Review card</h1>
        <p role="alert" className="form-error">
          {refusalText(query.error)}
        </p>
        <div className="row">
          <Button onClick={() => void query.refetch()}>Try again</Button>
          <Link to="/inbox">Back to the inbox</Link>
        </div>
      </Card>
    );
  }
  return <CardBody key={query.data.element.id} card={query.data} />;
}
